import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Building2, Search, Plus, Loader2, AlertTriangle, Check, X, Power, Wrench, ChevronRight } from "lucide-react";
import {
    fetchAdminCompanies,
    fetchAdminTradeCatalog,
    createAdminCompany,
    updateAdminCompany,
    formatApiError,
} from "@/lib/api";
import CompanyDetailModal from "@/components/admin/CompanyDetailModal";
import CreateCompanyDialog from "@/components/admin/CreateCompanyDialog";

const TRADE_BADGE_COLORS = {
    el: "bg-amber-100 text-amber-800 border-amber-200",
    vvs: "bg-sky-100 text-sky-800 border-sky-200",
    maler: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
    tomrer: "bg-emerald-100 text-emerald-800 border-emerald-200",
    ejendomsservice: "bg-slate-100 text-slate-800 border-slate-200",
};

const fmtDKK = (v) => new Intl.NumberFormat("da-DK", { maximumFractionDigits: 0 }).format(v || 0) + " kr.";

function TradeChip({ trade, name, isPrimary }) {
    const color = TRADE_BADGE_COLORS[trade] || "bg-slate-100 text-slate-700 border-slate-200";
    return (
        <span
            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${color} ${isPrimary ? "ring-2 ring-offset-1 ring-blue-400" : ""}`}
            data-testid={`kunde-trade-chip-${trade}`}
        >
            {isPrimary && <span className="text-blue-600">★</span>}
            {name}
        </span>
    );
}

export default function AdminKunder() {
    const [items, setItems] = useState([]);
    const [catalog, setCatalog] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [activeFilter, setActiveFilter] = useState("all"); // all | active | inactive
    const [tradeFilter, setTradeFilter] = useState("all");
    const [showCreate, setShowCreate] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = {};
            if (activeFilter === "active") params.active = true;
            if (activeFilter === "inactive") params.active = false;
            if (tradeFilter !== "all") params.trade = tradeFilter;
            if (q.trim()) params.q = q.trim();
            const [data, cat] = await Promise.all([
                fetchAdminCompanies(params),
                catalog.length ? Promise.resolve({ trades: catalog }) : fetchAdminTradeCatalog(),
            ]);
            setItems(data.items || []);
            if (!catalog.length) setCatalog(cat.trades || []);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [q, activeFilter, tradeFilter, catalog.length]);

    useEffect(() => {
        load();
    }, [load]);

    const tradeNameMap = useMemo(() => {
        const m = {};
        for (const t of catalog) m[t.id] = t.name;
        return m;
    }, [catalog]);

    const stats = useMemo(() => {
        const active = items.filter((i) => i.active).length;
        return {
            total: items.length,
            active,
            inactive: items.length - active,
            noTrades: items.filter((i) => !i.enabled_trades?.length).length,
        };
    }, [items]);

    const handleCreated = async () => {
        setShowCreate(false);
        toast.success("Virksomhed oprettet");
        await load();
    };

    const handleUpdated = async () => {
        toast.success("Virksomhed opdateret");
        await load();
    };

    const handleDeleted = async (result) => {
        toast.success("Virksomhed permanent slettet");
        setSelectedId(null);
        await load();
        console.info("[admin/kunde] tenant wiped:", result?.deleted);
    };

    return (
        <div className="space-y-6" data-testid="admin-kunder-page">
            {/* Header */}
            <div className="flex flex-wrap gap-3 justify-between items-start">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <Building2 className="w-6 h-6 text-blue-600" />
                        Kundevirksomheder
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Administrér tenants og styr hvilke fagområder hver virksomhed må modtage opgaver for via voice-intake.
                    </p>
                </div>
                <button
                    onClick={() => setShowCreate(true)}
                    data-testid="kunde-create-btn"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2.5 rounded-xl shadow-sm transition"
                >
                    <Plus className="w-4 h-4" />
                    Ny virksomhed
                </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard label="Alle" value={stats.total} testid="stat-total" />
                <StatCard label="Aktive" value={stats.active} testid="stat-active" accent="emerald" />
                <StatCard label="Deaktiverede" value={stats.inactive} testid="stat-inactive" accent="rose" />
                <StatCard label="Uden fagområder" value={stats.noTrades} testid="stat-no-trades" accent="amber" />
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        data-testid="kunde-search-input"
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Søg firma, CVR, email…"
                        className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
                    />
                </div>
                <select
                    data-testid="kunde-active-filter"
                    value={activeFilter}
                    onChange={(e) => setActiveFilter(e.target.value)}
                    className="rounded-xl border border-slate-200 text-sm px-3 py-2"
                >
                    <option value="all">Alle statusser</option>
                    <option value="active">Kun aktive</option>
                    <option value="inactive">Kun deaktiverede</option>
                </select>
                <select
                    data-testid="kunde-trade-filter"
                    value={tradeFilter}
                    onChange={(e) => setTradeFilter(e.target.value)}
                    className="rounded-xl border border-slate-200 text-sm px-3 py-2"
                >
                    <option value="all">Alle fagområder</option>
                    {catalog.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                </select>
            </div>

            {/* List */}
            {loading ? (
                <div className="bg-white rounded-2xl border border-slate-200 py-16 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 flex items-start gap-3" data-testid="kunde-error-banner">
                    <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                    <div className="flex-1 text-sm">{error}</div>
                    <button className="text-red-800 hover:underline text-sm" onClick={load}>Prøv igen</button>
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center" data-testid="kunde-empty-state">
                    <Building2 className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-600">Ingen virksomheder matcher dit filter.</p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto" data-testid="kunde-list">
                    <table className="w-full text-sm min-w-[1100px]">
                        <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wider">
                            <tr>
                                <th className="px-4 py-3 font-medium">Firma</th>
                                <th className="px-4 py-3 font-medium">Kontakt</th>
                                <th className="px-4 py-3 font-medium">CVR</th>
                                <th className="px-4 py-3 font-medium">Fagområder</th>
                                <th className="px-4 py-3 font-medium">Status</th>
                                <th className="px-4 py-3 font-medium">Plan</th>
                                <th className="px-4 py-3 font-medium">Pakke</th>
                                <th className="px-4 py-3 font-medium text-right">Netto MRR</th>
                                <th className="px-4 py-3 font-medium text-right">Profit/md.</th>
                                <th className="px-4 py-3 font-medium">Brugere</th>
                                <th className="px-4 py-3 font-medium">Oprettet</th>
                                <th className="px-4 py-3 w-16 text-right">Redigér</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {items.map((c) => (
                                <tr
                                    key={c.id}
                                    onClick={() => setSelectedId(c.id)}
                                    className="cursor-pointer hover:bg-slate-50 transition"
                                    data-testid={`kunde-row-${c.id}`}
                                >
                                    <td className="px-4 py-3 max-w-[220px]">
                                        <div className="font-medium text-slate-900 truncate" data-testid={`kunde-name-${c.id}`}>{c.name || "—"}</div>
                                        {(c.address || c.city) && (
                                            <div className="text-xs text-slate-500 truncate mt-0.5">{[c.address, c.postal_code, c.city].filter(Boolean).join(", ")}</div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 max-w-[180px]">
                                        <div className="text-sm text-slate-800 truncate">{c.contact_person || "—"}</div>
                                        <div className="text-xs text-slate-500 truncate">{c.email || "—"}</div>
                                        {c.phone && <div className="text-xs text-slate-500 truncate">{c.phone}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono text-slate-600 whitespace-nowrap">{c.cvr || "—"}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1 max-w-[220px]">
                                            {(c.enabled_trades || []).length === 0 && (
                                                <span className="text-xs text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Ingen aktive</span>
                                            )}
                                            {(c.enabled_trades || []).map((t) => (
                                                <TradeChip key={t} trade={t} name={tradeNameMap[t] || t} isPrimary={t === c.primary_trade} />
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {c.active ? (
                                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`kunde-status-${c.id}`}>
                                                <Check className="w-3 h-3" /> Aktiv
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200" data-testid={`kunde-status-${c.id}`}>
                                                <Power className="w-3 h-3" /> Deaktiveret
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{c.plan || c.subscription_status || "—"}</td>
                                    <td className="px-4 py-3 text-xs text-slate-700 whitespace-nowrap">
                                        {c.economics?.plan_label ? (
                                            <div>
                                                <div className="font-medium">{c.economics.plan_label}</div>
                                                <div className="text-slate-500">{c.economics.agreement_label}{c.economics.discount_pct > 0 && ` · –${Math.round(c.economics.discount_pct * 100)}%`}</div>
                                            </div>
                                        ) : (
                                            <span className="text-slate-400 italic">Ingen</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-mono text-slate-800 text-right whitespace-nowrap" data-testid={`kunde-mrr-${c.id}`}>{fmtDKK(c.economics?.netto_mrr)}</td>
                                    <td className="px-4 py-3 text-sm font-mono text-blue-700 text-right whitespace-nowrap" data-testid={`kunde-profit-${c.id}`}>{fmtDKK(c.economics?.monthly_profit)}</td>
                                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{c.user_count ?? 0}</td>
                                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                                        {c.created_at ? new Date(c.created_at).toLocaleDateString("da-DK") : "—"}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showCreate && (
                <CreateCompanyDialog
                    open={showCreate}
                    onClose={() => setShowCreate(false)}
                    catalog={catalog}
                    onCreated={handleCreated}
                    onSubmit={createAdminCompany}
                />
            )}
            {selectedId && (
                <CompanyDetailModal
                    open={!!selectedId}
                    companyId={selectedId}
                    catalog={catalog}
                    onClose={() => setSelectedId(null)}
                    onUpdated={handleUpdated}
                    onDeleted={handleDeleted}
                    onSubmit={updateAdminCompany}
                />
            )}
        </div>
    );
}

function StatCard({ label, value, testid, accent = "blue" }) {
    const accents = {
        blue: "bg-blue-50 text-blue-700",
        emerald: "bg-emerald-50 text-emerald-700",
        rose: "bg-rose-50 text-rose-700",
        amber: "bg-amber-50 text-amber-700",
    };
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid={testid}>
            <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl inline-flex items-center justify-center ${accents[accent]}`}>
                    <Wrench className="w-5 h-5" />
                </div>
                <div>
                    <p className="text-xs text-slate-500 font-medium">{label}</p>
                    <p className="text-xl font-bold text-slate-900">{value}</p>
                </div>
            </div>
        </div>
    );
}
